import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Star, ArrowRight, Clock, Shield, Zap, Award, DollarSign, Moon, Heart } from 'lucide-react';
import { businessInfo, services, reviews, whyChooseUs } from '../mockData';
import SEO from '../components/SEO';

const Home = () => {
  const heroImage = "https://images.unsplash.com/photo-1644088379091-d574269d422f";
  const servicesImage = "https://images.unsplash.com/photo-1559819774-16ebd5680048";

  const getIcon = (iconName) => {
    const icons = {
      Award, Zap, Shield, DollarSign, Moon, Heart
    };
    const IconComponent = icons[iconName];
    return IconComponent ? <IconComponent className="w-6 h-6" /> : null;
  };

  return (
    <div className="min-h-screen bg-black">
      <SEO 
        title="Evolution Cell Phone Repairs El Paso TX | Phone, Laptop, Computer & Game Console Repair"
        description="Expert device repair in El Paso TX. Same-day phone repair, laptop repair, computer repair & game console repair. Open until 2AM. Latino-owned. Call (915) 787-9448."
        keywords={[
          'cell phone repair El Paso',
          'iPhone repair El Paso',
          'laptop repair El Paso',
          'computer repair El Paso',
          'game console repair El Paso',
          'same day repair',
          'emergency phone repair',
          'screen replacement El Paso',
          'battery replacement El Paso'
        ]}
      />
      {/* Hero Section */}
      <section className="relative pt-24 pb-16 lg:pt-32 lg:pb-24 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={heroImage}
            alt="Professional phone repair"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-neutral-950 via-slate-950/95 to-neutral-950"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
                <Star className="w-4 h-4 text-white fill-white" />
                <span className="text-white font-semibold text-sm">{businessInfo.rating} Stars • {businessInfo.reviewCount}+ Reviews</span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Expert Device Repairs in <span className="text-white">El Paso</span>
              </h1>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Same-day repairs for phones, laptops, computers, and game consoles. Expert technicians, quality parts, and honest pricing. Open until 2:00 AM because tech problems don't wait.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href={`tel:${businessInfo.phone}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-black font-bold text-white font-semibold rounded-xl transition-all shadow-lg shadow-white/30 hover:shadow-white/50 hover:scale-105"
                >
                  <Phone className="w-5 h-5" />
                  Call Now: {businessInfo.phone}
                </a>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(businessInfo.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 hover:bg-neutral-700 text-white font-semibold rounded-xl transition-all border border-neutral-700"
                >
                  <MapPin className="w-5 h-5" />
                  Get Directions
                </a>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-neutral-900/50 rounded-lg border border-neutral-800">
                  <p className="text-3xl font-bold text-white">30min</p>
                  <p className="text-sm text-gray-400 mt-1">Avg Repair Time</p>
                </div>
                <div className="text-center p-4 bg-neutral-900/50 rounded-lg border border-neutral-800">
                  <p className="text-3xl font-bold text-white">2AM</p>
                  <p className="text-sm text-gray-400 mt-1">Open Until</p>
                </div>
                <div className="text-center p-4 bg-neutral-900/50 rounded-lg border border-neutral-800">
                  <p className="text-3xl font-bold text-white">Same Day</p>
                  <p className="text-sm text-gray-400 mt-1">Service</p>
                </div>
              </div>
            </div>

            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-white/20 rounded-2xl blur-3xl"></div>
                <img 
                  src={servicesImage}
                  alt="iPhone repair services"
                  className="relative rounded-2xl shadow-2xl border border-neutral-800"
                />
                <div className="absolute -bottom-6 -right-6 bg-neutral-900 border border-neutral-800 rounded-xl p-4 shadow-xl">
                  <p className="text-sm text-gray-400 mb-1">Walk-Ins Welcome</p>
                  <p className="text-2xl font-bold text-white">Open Now</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-neutral-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Why Choose Evolution Cell Phone Repairs?
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Your trusted phone repair experts in El Paso, TX
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index}
                className="p-6 bg-neutral-900 border border-neutral-800 rounded-xl hover:border-white/50 transition-all hover:shadow-lg hover:shadow-white/10"
              >
                <div className="w-12 h-12 bg-white/10 rounded-lg flex items-center justify-center text-white mb-4">
                  {getIcon(item.icon)}
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Our Services
              </h2>
              <p className="text-gray-400 text-lg">
                Professional repair services for all your devices
              </p>
            </div>
            <Link 
              to="/services"
              className="hidden sm:inline-flex items-center gap-2 text-white hover:text-white-300 font-semibold transition-colors"
            >
              View All Services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.slice(0, 8).map((service) => (
              <div 
                key={service.id}
                className="p-6 bg-neutral-900 border border-neutral-800 rounded-xl hover:border-white/50 transition-all hover:shadow-lg hover:shadow-white/10 group"
              >
                <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-white transition-colors">
                  {service.name}
                </h3>
                <p className="text-gray-400 text-sm mb-4">{service.description}</p>
                <div className="flex justify-between items-center">
                  <span className="text-white font-semibold">{service.price}</span>
                  <span className="text-gray-500 text-sm">{service.duration}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link 
              to="/services"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-neutral-700 text-white font-semibold rounded-lg transition-colors border border-neutral-700"
            >
              View All Services
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-16 bg-neutral-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              What Our Customers Say
            </h2>
            <div className="flex items-center justify-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 text-white fill-white" />
                ))}
              </div>
              <span className="text-gray-400 text-lg">{businessInfo.rating} out of 5 stars</span>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {reviews.slice(0, 3).map((review) => (
              <div 
                key={review.id}
                className="p-6 bg-neutral-900 border border-neutral-800 rounded-xl"
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-white">{review.name}</h4>
                    <p className="text-gray-500 text-sm">{review.date}</p>
                  </div>
                  <div className="flex">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-white fill-white" />
                    ))}
                  </div>
                </div>
                <p className="text-gray-300">{review.text}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link 
              to="/reviews"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-100 text-black font-semibold rounded-lg transition-colors shadow-lg shadow-white/20"
            >
              Read All Reviews
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-teal-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 lg:p-12 text-center">
            <Clock className="w-16 h-16 text-white mx-auto mb-6" />
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Need Your Phone Fixed Today?
            </h2>
            <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
              Walk-ins welcome! Most repairs completed same-day. Open until 2:00 AM to serve you better.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={`tel:${businessInfo.phone}`}
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white hover:bg-gray-100 text-black font-bold text-white font-semibold rounded-xl transition-all shadow-lg shadow-white/30 hover:shadow-white/50 hover:scale-105"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-slate-800 hover:bg-neutral-700 text-white font-semibold rounded-xl transition-all border border-neutral-700"
              >
                Contact Us
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
